import { useState, type FormEvent, type ReactElement } from 'react';
import { toast } from 'sonner';
import { useMyCapabilities } from '../utils/metadata/capabilities';
import { handleOperationError, insertRecord } from '../utils/supabase-operations';
import EnhancedDialog from './EnhancedDialog';
import './CreateListRecordButton.css';

export type CreatableListTable = 'merchant' | 'funder' | 'rep' | 'lead_source';

interface CreateRecordConfig {
  recordLabel: string;
  fieldName: string;
  fieldLabel: string;
  placeholder: string;
  capability: string;
}

const CREATE_RECORD_CONFIG: Record<CreatableListTable, CreateRecordConfig> = {
  merchant: {
    recordLabel: 'Merchant',
    fieldName: 'business_name',
    fieldLabel: 'Business name',
    placeholder: 'Enter the business name',
    capability: 'create_merchant',
  },
  funder: {
    recordLabel: 'Funder',
    fieldName: 'name',
    fieldLabel: 'Funder name',
    placeholder: 'Enter the funder name',
    capability: 'create_funder',
  },
  rep: {
    recordLabel: 'Rep',
    fieldName: 'name',
    fieldLabel: 'Rep name',
    placeholder: 'Enter the rep name',
    capability: 'create_rep',
  },
  lead_source: {
    recordLabel: 'Lead Source',
    fieldName: 'source',
    fieldLabel: 'Source name',
    placeholder: 'Enter the lead source name',
    capability: 'create_lead_source',
  },
};

interface CreatedListRecord {
  id: string | number;
  label: string;
}

interface CreateListRecordButtonProps {
  tableName: CreatableListTable;
  onCreated?: (record: CreatedListRecord) => void | Promise<void>;
}

/**
 * Minimal creation flow for reference-data list pages. Creating only the
 * identifying field keeps this fast; the newly created record then uses the
 * table's existing detail experience for optional fields.
 */
export default function CreateListRecordButton({
  tableName,
  onCreated,
}: CreateListRecordButtonProps): ReactElement | null {
  const { hasCapability, isLoading: capabilitiesLoading } = useMyCapabilities();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const config = CREATE_RECORD_CONFIG[tableName];

  if (capabilitiesLoading || !hasCapability(config.capability)) return null;

  const close = () => {
    if (saving) return;
    setOpen(false);
    setValue('');
  };

  const create = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const label = value.trim();
    if (!label || saving) return;

    setSaving(true);
    try {
      const result = await insertRecord({
        tableName,
        schema: 'public',
        data: { [config.fieldName]: label },
        options: { invalidateCache: true },
      });
      if (result.error) throw result.error;

      const created = result.data?.[0];
      if (created?.id == null) throw new Error(`${config.recordLabel} was created but no ID was returned.`);

      setOpen(false);
      setValue('');
      toast.success(`${config.recordLabel} created`);
      try {
        await onCreated?.({ id: created.id, label });
      } catch (refreshError) {
        console.error(`Failed to refresh after creating ${tableName}:`, refreshError);
        toast.warning(`${config.recordLabel} was created, but the list could not refresh.`);
      }
    } catch (error) {
      const info = handleOperationError(error, `Failed to create ${config.recordLabel.toLowerCase()}`);
      toast.error(info.userMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="sky-btn sky-btn-primary sky-list-create-button"
        onClick={() => setOpen(true)}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
          <path d="M12 5v14M5 12h14" />
        </svg>
        Create {config.recordLabel}
      </button>

      <EnhancedDialog
        isOpen={open}
        onClose={close}
        title={`Create ${config.recordLabel}`}
        canEscapeKeyClose={!saving}
        canOutsideClickClose={!saving}
      >
        <form onSubmit={(event) => void create(event)}>
          <div className="sky-dialog-body sky-list-create-dialog-body">
            <label htmlFor={`create-${tableName}-name`}>{config.fieldLabel}</label>
            <input
              id={`create-${tableName}-name`}
              value={value}
              onChange={(event) => setValue(event.target.value)}
              placeholder={config.placeholder}
              disabled={saving}
              autoFocus
            />
            <p>You can fill in the remaining details after this record is created.</p>
          </div>
          <div className="sky-dialog-footer">
            <button type="button" className="sky-btn sky-btn-ghost" onClick={close} disabled={saving}>Cancel</button>
            <button type="submit" className="sky-btn sky-btn-primary" disabled={!value.trim() || saving}>
              {saving ? 'Creating…' : `Create ${config.recordLabel}`}
            </button>
          </div>
        </form>
      </EnhancedDialog>
    </>
  );
}
